import * as Tabs from '@radix-ui/react-tabs'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Separator } from '@/shared/components/ui/separator'
import { Badge } from '@/shared/components/ui/badge'
import { BookMarked, ChevronDown, ChevronUp } from 'lucide-react'
import { useStoryWriterStore } from '../store/storyWriterStore'
import { useStoryHistory } from '../hooks/useStoryHistory'
import { StoryWriterForm } from './StoryWriterForm'
import { StoryOutput } from './StoryOutput'
import { StoryHistory } from './StoryHistory'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'

const COMING_SOON_TABS = [
  { value: 'pr-description', label: 'PR Description' },
  { value: 'review-comments', label: 'Review Comments' },
  { value: 'epic-brief', label: 'Epic Brief' },
]

export function JiraToolbeltTabs() {
  const { inputText, outputText, status, inputTokens, outputTokens } = useStoryWriterStore()
  const { save } = useStoryHistory()
  const [historyOpen, setHistoryOpen] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleSave = useCallback(async () => {
    if (!outputText || !inputText) return
    await save({
      draftNotes: inputText,
      generatedStories: outputText,
      inputTokens: inputTokens ?? null,
      outputTokens: outputTokens ?? null,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }, [inputText, outputText, inputTokens, outputTokens, save])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && status === 'done') {
        e.preventDefault()
        void handleSave()
      }
    },
    [handleSave, status]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return (
    <Tabs.Root defaultValue="story-writer">
      <Tabs.List className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground">
        <Tabs.Trigger
          value="story-writer"
          className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
        >
          Story Writer
        </Tabs.Trigger>
        {COMING_SOON_TABS.map((tab) => (
          <Tabs.Trigger
            key={tab.value}
            value={tab.value}
            disabled
            className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 gap-1.5"
          >
            {tab.label}
            <Badge variant="outline" className="text-[10px] px-1 py-0 font-normal">
              Soon
            </Badge>
          </Tabs.Trigger>
        ))}
      </Tabs.List>

      <Tabs.Content value="story-writer" className="mt-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Story Writer</h2>
            <p className="text-muted-foreground text-sm mt-0.5">
              Turn draft notes into formatted Jira user stories
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHistoryOpen((o) => !o)}
            className="gap-2"
          >
            <BookMarked className="h-4 w-4" />
            History
            {historyOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
        </div>

        {historyOpen && (
          <div className="rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold mb-3">Recent generations</h3>
            <ErrorBoundary>
              <StoryHistory />
            </ErrorBoundary>
          </div>
        )}

        <ErrorBoundary>
          <StoryWriterForm />
        </ErrorBoundary>

        {(status === 'streaming' || status === 'done' || status === 'aborted') && outputText && (
          <>
            <Separator />
            <StoryOutput />

            {status === 'done' && (
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => void handleSave()} disabled={saved}>
                  {saved ? 'Saved!' : 'Save to history'}
                  <span className="ml-1 text-xs text-muted-foreground">&#8984;S</span>
                </Button>
              </div>
            )}
          </>
        )}

        {status === 'error' && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            Something went wrong. Please try again.
          </div>
        )}
      </Tabs.Content>
    </Tabs.Root>
  )
}
